<template>
  <div class="flex flex-col w-full bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-all duration-200 mb-4">
    <!-- Header del panel -->
    <div
      class="flex items-center px-4 py-3.5 border-b border-gray-100 hover:bg-gray-50 transition-colors duration-150"
      :class="{ 'cursor-pointer group': canExpand }"
      @click="_handleHeaderClick"
    >
      <!-- Icono a la izquierda -->
      <div class="flex-shrink-0 mr-3">
        <b-icon
          pack="fas"
          :icon="item?.icon || 'cog'"
          class="text-blue-600 text-base"
        />
      </div>

      <!-- Centro: Título/Descripción y Select/Switch (responsive) -->
      <div
        class="flex-1 grid items-center gap-2 sm:gap-4"
        :class="showOptionInHeader ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'"
      >
        <!-- Título y Descripción -->
        <div class="flex flex-col" :class="showOptionInHeader ? '' : 'w-full'">
          <span class="font-medium text-sm text-gray-800">
            {{ item?.name || 'Opción' }}
          </span>
          <span
            v-if="item?.description"
            class="text-xs text-gray-500 font-normal mt-0.5"
          >
            {{ item.description }}
          </span>
        </div>

        <!-- Switch/Select (según modo) -->
        <div
          v-if="showOptionInHeader"
          class="flex items-center space-x-2 justify-start sm:justify-end"
          @click.stop="_handleOptionClick"
        >
          <span v-if="optionType === 'switch'" class="text-xs text-gray-500">{{ isActive ? 'Activado' : 'Desactivado' }}</span>
          <b-switch
            v-if="optionType === 'switch'"
            v-model="isActive"
            @input="_handleSwitchChange"
            size="is-small"
          />
          <b-select
            v-else-if="optionType === 'select'"
            v-model="currentSelectedValue"
            @input="_handleSelectChange"
            size="is-small"
          >
            <option v-for="(item, i) in selectOptions"
              :value="item.value" :key="'so_'+i">{{ item.text }}</option>
          </b-select>

          <b-button
            v-else-if="optionType === 'button'"
            :type="buttonOptions.type || 'is-light'"
            :size="buttonOptions.size || 'is-small'"
            :icon-pack="buttonOptions.iconPack || 'fas'"
            :icon-left="buttonOptions.icon || null"
            :disabled="buttonOptions.disabled || false"
            @click="_handleButtonClick"
          >
            {{ buttonOptions.label }}
          </b-button>
        </div>
      </div>

      <!-- Chevron a la derecha -->
      <div
        v-if="showChevron"
        class="flex items-center justify-center flex-shrink-0 ml-3"
      >
        <b-icon
          pack="fas"
          :icon="isExpanded ? 'chevron-up' : 'chevron-down'"
          class="text-gray-400 text-xs transition-transform duration-200 group-hover:text-gray-600"
          :class="{ 'transform rotate-180': isExpanded }"
        />
      </div>
    </div>

    <!-- Contenido desplegable -->
    <transition name="slide-down">
      <div v-show="isExpanded && canExpand" class="px-4 py-4">
        <slot></slot>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'ExpandablePanel',

  props: {
    item: {
      type: Object,
      default: null
    },
    defaultExpanded: {
      type: Boolean,
      default: false
    },
    defaultActive: {
      type: Boolean,
      default: false
    },
    defaultSelectedValue: {
      // Para select: valor inicial
      type: [String, Number],
      default: null
    },
    mode: {
      type: String,
      default: 'expandable',
      validator: (value) => ['expandable', 'option-only', 'mixed'].includes(value)
    },
    optionType: {
      type: String,
      default: 'switch',
      validator: (value) => ['switch', 'select', 'button'].includes(value)
    },
    selectOptions: {
      type: Array,
      default: () => []
    },
    buttonOptions: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      isExpanded: this.defaultExpanded,
      isActive: this.defaultActive,
      currentSelectedValue: this.defaultSelectedValue,
    };
  },

  computed: {
    canExpand() {
      // En modo 'option-only' no se puede expandir
      if (this.mode === 'option-only') return false;

      return true;
    },

    showChevron() {
      // Solo ocultar chevron en modo 'option-only'
      // En los otros modos (expandable, mixed) siempre se muestra
      return this.mode !== 'option-only';
    },

    showOptionInHeader() {
      // Modo 'option-only': siempre mostrar en header
      if (this.mode === 'option-only') return true;

      // Modo 'mixed': siempre mostrar en header (incluso cuando está expandido)
      if (this.mode === 'mixed') return true;

      // Modo 'expandable': no mostrar en header
      return false;
    }
  },

  watch: {
    defaultActive(newVal) {
      this.isActive = newVal;
    },
    defaultSelectedValue(newVal) {
      this.currentSelectedValue = newVal;
    }
  },

  methods: {
    _handleHeaderClick() {
      if (this.canExpand) {
        this._toggleExpand();
      }
    },

    _toggleExpand() {
      this.isExpanded = !this.isExpanded;
      this.$emit('toggle', this.isExpanded);
    },

    _handleOptionClick(event) {
      // Prevenir que el click en la opción expanda/colapse el panel
      event.stopPropagation();
    },

    _handleSwitchChange(value) {
      this.isActive = value;
      this.$emit('active-change', value);
      this.$emit('input', value);
    },

    _handleSelectChange(value) {
      this.currentSelectedValue = value;
      this.$emit('select-change', value);
      this.$emit('update:selectedValue', value);
    },

    _handleButtonClick() {
      // Emit ambos para flexibilidad: 'button-click' es semánticamente claro,
      // 'click' es la convención corta. El padre escucha el que prefiera.
      this.$emit('button-click');
      this.$emit('click');
    },
  }
}
</script>

<style scoped>
/* Transición para el despliegue */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
  overflow: hidden;
}

.slide-down-enter,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}

.slide-down-enter-to,
.slide-down-leave {
  max-height: 500px;
  opacity: 1;
}
</style>
